import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCoupons, createCoupon, updateCoupon, deleteCoupon, fetchCouponDetails } from '../store/slices/couponSlice';
import { RootState, AppDispatch } from '../store';
import { PlusIcon, PencilIcon, TrashIcon, XMarkIcon, TagIcon, EyeIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface CouponFormData {
  couponCode: string;
  title: string;
  description: string;
  worthAmount: string;
  expiryDate: string;
  discountType: 'flat' | 'percentage';
  minCartValue: string;
  isActive: boolean;
}

const Coupons: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { coupons = [], selectedCoupon, isLoading } = useSelector((state: RootState) => state.coupons);
  
  const [showModal, setShowModal] = useState(false);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<any>(null);
  const [formData, setFormData] = useState<CouponFormData>({
    couponCode: '',
    title: '',
    description: '',
    worthAmount: '',
    expiryDate: '',
    discountType: 'flat',
    minCartValue: '0',
    isActive: true
  });

  useEffect(() => {
    dispatch(fetchCoupons());
  }, [dispatch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const couponData = {
      ...formData,
      worthAmount: parseFloat(formData.worthAmount),
      minCartValue: parseFloat(formData.minCartValue)
    };

    try {
      if (editingCoupon) {
        await dispatch(updateCoupon({ id: editingCoupon._id, data: couponData })).unwrap();
        toast.success('Coupon updated successfully');
      } else {
        await dispatch(createCoupon(couponData)).unwrap();
        toast.success('Coupon created successfully');
      }
      handleCloseModal();
      dispatch(fetchCoupons());
    } catch (error: any) {
      toast.error(error.message || 'Operation failed');
    }
  };

  const handleViewCoupon = async (couponId: string) => {
    try {
      await dispatch(fetchCouponDetails(couponId)).unwrap();
      setShowCouponModal(true);
    } catch (error: any) {
      toast.error(error.message || 'Failed to fetch coupon details');
    }
  };

  const handleCloseCouponModal = () => {
    setShowCouponModal(false);
  };

  const handleEdit = (coupon: any) => {
    setEditingCoupon(coupon);
    setFormData({
      couponCode: coupon.couponCode,
      title: coupon.title,
      description: coupon.description,
      worthAmount: coupon.worthAmount.toString(),
      expiryDate: new Date(coupon.expiryDate).toISOString().split('T')[0],
      discountType: coupon.discountType,
      minCartValue: coupon.minCartValue.toString(),
      isActive: coupon.isActive
    });
    setShowModal(true);
  };

  const handleDelete = async (couponId: string) => {
    if (window.confirm('Are you sure you want to delete this coupon?')) {
      try {
        await dispatch(deleteCoupon(couponId)).unwrap();
        toast.success('Coupon deleted successfully');
      } catch (error: any) {
        toast.error(error.message || 'Failed to delete coupon');
      }
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCoupon(null);
    setFormData({
      couponCode: '',
      title: '',
      description: '',
      worthAmount: '',
      expiryDate: '',
      discountType: 'flat',
      minCartValue: '0',
      isActive: true
    });
  };

  const isExpired = (date: string) => new Date(date) < new Date();

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Coupons Management</h1>
          <p className="mt-2 text-sm text-gray-700">Manage all discount coupons</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Coupon
        </button>
      </div>

      {/* Coupons Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Coupons ({Array.isArray(coupons) ? coupons.length : 0})
          </h3>
        </div>
        
        {isLoading ? (
          <div className="p-6 text-center">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Coupon
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Discount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Min Cart Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Expiry
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Array.isArray(coupons) && coupons.filter(Boolean).map((coupon: any) => (
                  <tr key={coupon._id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <TagIcon className="h-8 w-8 text-blue-500" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{coupon?.couponCode}</div>
                          <div className="text-sm text-gray-500">{coupon?.title}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {coupon?.discountType === 'percentage' ? `${coupon?.worthAmount}%` : `₹${coupon?.worthAmount}`}
                      </div>
                      <div className="text-sm text-gray-500">{coupon?.discountType}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ₹{coupon?.minCartValue || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {coupon?.expiryDate ? new Date(coupon.expiryDate).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        !coupon?.isActive ? 'bg-red-100 text-red-800' :
                        isExpired(coupon?.expiryDate) ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {!coupon?.isActive ? 'Inactive' :
                         isExpired(coupon?.expiryDate) ? 'Expired' : 'Active'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => handleViewCoupon(coupon._id)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleEdit(coupon)}
                          className="text-green-600 hover:text-green-900"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(coupon._id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {Array.isArray(coupons) && coupons.length === 0 && !isLoading && (
          <div className="p-6 text-center text-gray-500">
            No coupons found. Create your first coupon!
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseModal}></div>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      {editingCoupon ? 'Edit Coupon' : 'Add New Coupon'}
                    </h3>
                    <button type="button" onClick={handleCloseModal}>
                      <XMarkIcon className="h-6 w-6 text-gray-400" />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Coupon Code</label>
                      <input
                        type="text"
                        required
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={formData.couponCode}
                        onChange={(e) => setFormData({...formData, couponCode: e.target.value.toUpperCase()})}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Title</label>
                      <input
                        type="text"
                        required
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={formData.title}
                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Description</label>
                      <textarea
                        required
                        rows={3}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Discount Type</label>
                      <select
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={formData.discountType}
                        onChange={(e) => setFormData({...formData, discountType: e.target.value as 'flat' | 'percentage'})}
                      >
                        <option value="flat">Flat Amount</option>
                        <option value="percentage">Percentage</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        {formData.discountType === 'percentage' ? 'Discount %' : 'Discount Amount'}
                      </label>
                      <input
                        type="number"
                        required
                        min="1"
                        max={formData.discountType === 'percentage' ? '100' : undefined}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={formData.worthAmount}
                        onChange={(e) => setFormData({...formData, worthAmount: e.target.value})}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Min Cart Value</label>
                      <input
                        type="number"
                        min="0"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={formData.minCartValue}
                        onChange={(e) => setFormData({...formData, minCartValue: e.target.value})}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Expiry Date</label>
                      <input
                        type="date"
                        required
                        min={new Date().toISOString().split('T')[0]}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={formData.expiryDate}
                        onChange={(e) => setFormData({...formData, expiryDate: e.target.value})}
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                          checked={formData.isActive}
                          onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                        />
                        <span className="ml-2 text-sm text-gray-700">Active</span>
                      </label>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {editingCoupon ? 'Update' : 'Create'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Coupon Details Modal */}
      {showCouponModal && selectedCoupon && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseCouponModal}></div>
            
            <div className="inline-block align-middle bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all max-w-2xl w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Coupon Details</h3>
                  <button type="button" onClick={handleCloseCouponModal}>
                    <XMarkIcon className="h-6 w-6 text-gray-400" />
                  </button>
                </div>
                
                <div className="space-y-6">
                  <div className="text-center">
                    <TagIcon className="mx-auto h-16 w-16 text-blue-500 mb-4" />
                    <h4 className="text-2xl font-bold text-gray-900">{selectedCoupon.couponCode}</h4>
                    <p className="text-lg text-gray-600">{selectedCoupon.title}</p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-bold text-gray-700">Description</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedCoupon.description}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-bold text-gray-700">Discount Type</label>
                      <p className="mt-1 text-sm text-gray-900 capitalize">{selectedCoupon.discountType}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-bold text-gray-700">Discount Value</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {selectedCoupon.discountType === 'percentage' 
                          ? `${selectedCoupon.worthAmount}%` 
                          : `₹${selectedCoupon.worthAmount}`}
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-bold text-gray-700">Min Cart Value</label>
                      <p className="mt-1 text-sm text-gray-900">₹{selectedCoupon.minCartValue || 0}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-bold text-gray-700">Expiry Date</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {selectedCoupon.expiryDate ? new Date(selectedCoupon.expiryDate).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-bold text-gray-700">Status</label>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        !selectedCoupon.isActive ? 'bg-red-100 text-red-800' :
                        isExpired(selectedCoupon.expiryDate) ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {!selectedCoupon.isActive ? 'Inactive' :
                         isExpired(selectedCoupon.expiryDate) ? 'Expired' : 'Active'}
                      </span>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-bold text-gray-700">Created Date</label>
                      <p className="mt-1 text-sm text-gray-900">{new Date(selectedCoupon.createdAt).toLocaleDateString()}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-bold text-gray-700">Last Updated</label>
                      <p className="mt-1 text-sm text-gray-900">{new Date(selectedCoupon.updatedAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleCloseCouponModal}
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:w-auto sm:text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Coupons;